import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from "react";
import { auth } from "../config/firebase";
import { onAuthStateChanged, onIdTokenChanged } from "firebase/auth";
import { getUserProfile, UserProfile } from "../services/api/user.service";
import { toast } from "react-toastify";
import { performLogout } from "../utils/logoutHandler";

interface UserContextType {
  userProfile: UserProfile | null;
  loading: boolean;
  refreshProfile: () => Promise<void>;
  isProfileComplete: boolean;
  setSignupInProgress: (inProgress: boolean) => void;
  triggerProfileFetch: () => void;
  shouldRedirectToConfirm: boolean; // Only true after initial login check finds incomplete profile
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [signupInProgress, setSignupInProgress] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [shouldRedirectToConfirm, setShouldRedirectToConfirm] = useState(false); // Only set to true once after initial login check
  const refreshPromiseRef = useRef<Promise<void> | null>(null);
  const hasCheckedProfileThisSession = useRef(false); // Track if we've checked profile completeness in this session
  const wasLoggedOutOnMount = useRef<string | null>(null); // Capture the initial wasLoggedOut value BEFORE onAuthStateChanged modifies it
  const [teamStatusReady, setTeamStatusReady] = useState(false);
  const [hasNonOwnerTeam, setHasNonOwnerTeam] = useState(false);

  const fetchUserProfile = async () => {
    // Validate token on component mount/refresh - event-driven, no interval
    // This ensures token is checked when UserContext loads or refreshes
    try {
      const user = auth.currentUser;
      if (user) {
        // Quick token check - uses cached token if valid (no network call)
        // Only makes network call if token needs refresh
        await user.getIdToken(false);
      }
    } catch (tokenError: any) {
      // Token invalid - will be caught by API call's 401 response below
    }
    
    try {
      const profile = await getUserProfile();
      //console.log(profile, "profiletesting");
      const roleid = profile.role_id;
      sessionStorage.setItem('roleid', roleid.toString());
      // Store systemRole if available
      if (profile.systemRole) {
        sessionStorage.setItem('systemRole', profile.systemRole);
      }
      if (profile.isSuperAdmin !== undefined) {
        sessionStorage.setItem(
          'isSuperAdmin',
          profile.isSuperAdmin === true || profile.isSuperAdmin === 1 || profile.isSuperAdmin === '1'
            ? '1'
            : '0'
        );
      }
      setUserProfile(profile);
    } catch (error: any) {
      // console.error("Error fetching user profile:", error);
      console.warn("Error fetching user profile:", error);
      
      // Handle database connection errors (500) - don't logout, just show error
      if (error?.status === 500 && error?.isDatabaseError) {
        // Don't clear profile or logout - user is still authenticated
        // Just return and let the user try again
        return;
      }
      
      // Handle 401 Unauthorized errors (real auth failures)
      if (error?.status === 401 || error?.isUnauthorized || (error?.message && error.message.includes("Unauthorized"))) {
        setUserProfile(null);
        // Use centralized logout handler (prevents race conditions)
        await performLogout("401 Unauthorized from profile fetch");
        return;
      }
      
      setUserProfile(null);
    }
  };

  const refreshProfile = useCallback(async () => {
    // If already refreshing, return the existing promise to prevent concurrent calls
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    // If refresh is in progress, wait for it
    if (isRefreshing) {
      return refreshPromiseRef.current || Promise.resolve();
    }

    setIsRefreshing(true);
    const refreshPromise = fetchUserProfile().finally(() => {
      setIsRefreshing(false);
      refreshPromiseRef.current = null;
    });

    refreshPromiseRef.current = refreshPromise;
    return refreshPromise;
  }, []); // fetchUserProfile and setIsRefreshing are stable, no dependencies needed

  const triggerProfileFetch = () => {
    // Manually trigger profile fetch after signup is complete
    fetchUserProfile();
  };

  useEffect(() => {
    // Capture the initial wasLoggedOut value BEFORE onAuthStateChanged can modify it
    // This is critical to distinguish between fresh login vs page reload
    wasLoggedOutOnMount.current = sessionStorage.getItem('wasLoggedOut');
    
    // Initialize: Check if this is a fresh login (user was logged out) vs page reload
    // sessionStorage persists only for the current tab session
    // On page reload with user still logged in, this will be 'false' (set on previous login)
    // On fresh login, this will be 'true' (set on logout)
    // On first load (no previous session), check if user is already authenticated
    const isFirstLoad = wasLoggedOutOnMount.current === null;
    
    // If first load and user is already authenticated (persisted session), 
    // set wasLoggedOut to 'false' to prevent checking on reload
    if (isFirstLoad && auth.currentUser) {
      sessionStorage.setItem('wasLoggedOut', 'false');
      wasLoggedOutOnMount.current = 'false';
    }
    
    // Use centralized logout handler (prevents race conditions)
    const handleLogout = async (reason: string = "Session expired") => {
      setUserProfile(null);
      await performLogout(reason);
    };
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Only fetch profile if signup is not in progress
        // This prevents calling /users/me before backend signup is complete
        if (!signupInProgress) {
          await fetchUserProfile();
        }
        
        // Mark that user is now logged in (prevents checking on next reload)
        // Only set this AFTER we've checked wasLoggedOutOnMount for the current session
        sessionStorage.setItem('wasLoggedOut', 'false');
      } else {
        setUserProfile(null);
        setSignupInProgress(false);
        // Reset redirect flag and check flag when user logs out
        setShouldRedirectToConfirm(false);
        hasCheckedProfileThisSession.current = false;
        // Mark that user is logged out (so next login will be detected as fresh)
        sessionStorage.setItem('wasLoggedOut', 'true');
        wasLoggedOutOnMount.current = 'true'; // Update ref for next login
      }
      setLoading(false);
    });

    // Listen for token changes - this detects when token is revoked/invalidated
    const unsubscribeToken = onIdTokenChanged(auth, async (user) => {
      if (user) {
        try {
          // Try to get a fresh token - if this fails, the token is invalid
          // Force refresh to immediately detect password changes or token revocations
          await user.getIdToken(true); // Force refresh
          
          // If token refresh succeeds, verify it's still valid by checking if it can be decoded
          // Firebase automatically refreshes tokens, so if getIdToken() succeeds, token is valid
        } catch (error: any) {
          console.error("[Auth] Token refresh failed:", error);
          
          // Token refresh failed - user's session is invalid
          // Common errors: auth/user-token-expired, auth/invalid-user-token, auth/user-disabled
          if (
            error?.code === "auth/user-token-expired" ||
            error?.code === "auth/invalid-user-token" ||
            error?.code === "auth/user-disabled" ||
            error?.code === "auth/user-not-found" ||
            error?.code === "auth/wrong-password" ||
            error?.message?.includes("token") ||
            error?.message?.includes("expired") ||
            error?.message?.includes("invalid")
          ) {
            await handleLogout("Authentication token expired or invalid");
          }
        }
      } else {
        // User is null - already logged out or never logged in
        setUserProfile(null);
        setSignupInProgress(false);
      }
    });

    // Event-driven token validation - no intervals to avoid rate limits
    // We validate tokens only on specific events:
    // 1. Route changes (handled in App.tsx)
    // 2. API 401 errors (handled in client.ts)
    // 3. Component mount/refresh (handled here via onAuthStateChanged/onIdTokenChanged)
    // 4. Form submissions (each component handles)
    // This approach avoids rate limits by not polling continuously

    const onTeamStatusReady = (event: Event) => {
      const detail = (event as CustomEvent)?.detail || {};
      if (typeof detail.hasNonOwnerTeam === 'boolean') {
        setHasNonOwnerTeam(detail.hasNonOwnerTeam);
        setTeamStatusReady(true);
      }
    };

    window.addEventListener('teamStatusReady', onTeamStatusReady);

    return () => {
      unsubscribeAuth();
      unsubscribeToken();
      window.removeEventListener('teamStatusReady', onTeamStatusReady);
    };
  }, [signupInProgress]);

  // Check if the profile is complete - only show confirm page if essential fields are missing
  // Helper function to safely check if a string field has a value
  const hasValue = (value: string | null | undefined): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value !== 'string') return false;
    return value.trim().length > 0;
  };

  // Check if artist array exists and has at least one artist
  const hasArtist = (): boolean => {
    return !!(userProfile?.artist && Array.isArray(userProfile.artist) && userProfile.artist.length > 0);
  };

  const isProfileComplete = (() => {
    // While loading, don't show confirm page
    if (!userProfile || loading) {
      return true;
    }

    // Check if artist array exists and has data
    if (!userProfile.artist || !Array.isArray(userProfile.artist) || userProfile.artist.length === 0) {
      return false;
    }

    // TypeScript now knows artist exists and has at least one element
    const artist = userProfile.artist[0];
    
    // Match public signup required fields (address/shirt/geo not required for confirm redirect)
    // spotify_url is optional here — empty spotify_url does not trigger confirm-details redirect
    const checks = {
      artist_name: hasValue(artist?.artist_name),
      distributor: hasValue(artist?.distributor),
      mobile: hasValue(userProfile.mobile),
    };

    // Log which fields are missing (only in development)
    if (process.env.NODE_ENV === 'development') {
      const missingFields = Object.entries(checks)
        .filter(([_, isValid]) => !isValid)
        .map(([field, _]) => field);
      
      if (missingFields.length > 0) {
        // Missing fields detected - profile is incomplete
      }
    }

    // All fields must be present and valid
    return Object.values(checks).every(check => check === true);
  })();

  // Set redirect flag ONLY on fresh login (not on page reload)
  useEffect(() => {
    if (!loading && userProfile && !signupInProgress) {
      // Use the captured value from mount (BEFORE onAuthStateChanged modified it)
      // This distinguishes between fresh login vs page reload
      const wasLoggedOut = wasLoggedOutOnMount.current === 'true';

      // Prefer event-driven state; fall back to sessionStorage if event not ready yet
      const effectiveHasNonOwnerTeam = teamStatusReady
        ? hasNonOwnerTeam
        : sessionStorage.getItem('hasNonOwnerTeam') === 'true';

      // If we haven't received team status yet, wait before deciding
      if (!teamStatusReady && !signupInProgress) {
        return;
      }

      // Only check profile completeness if:
      // 1. This is a fresh login (wasLoggedOut === true)
      // 2. We haven't checked this session yet
      // 3. User is not a non-owner team member (team members can skip)
      if (wasLoggedOut && !hasCheckedProfileThisSession.current) {
        // Mark that we've checked this session
        hasCheckedProfileThisSession.current = true;
        
        if (!isProfileComplete && !effectiveHasNonOwnerTeam) {
          setShouldRedirectToConfirm(true);
        } else {
          setShouldRedirectToConfirm(false);
        }
      } else if ((isProfileComplete || effectiveHasNonOwnerTeam) && shouldRedirectToConfirm) {
        // Clear redirect flag if profile becomes complete or user is a team member
        setShouldRedirectToConfirm(false);
      }
    }
  }, [loading, userProfile, isProfileComplete, signupInProgress, teamStatusReady, hasNonOwnerTeam]); // Don't include shouldRedirectToConfirm to avoid loops

  return (
    <UserContext.Provider 
      value={{ 
        userProfile, 
        loading, 
        refreshProfile, 
        isProfileComplete,
        setSignupInProgress,
        triggerProfileFetch,
        shouldRedirectToConfirm // Expose redirect flag
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
